/**
 * Lista blanca para el HTML que llega a la página desde una respuesta del LLM o desde lo que el
 * usuario escribe en el chat (mensajes y canvas del Tutor AI).
 *
 * Ese HTML no es de fiar: una inyección de instrucciones en un texto pegado o en una lección puede
 * hacer que el modelo devuelva `<iframe srcdoc="<script>…">`, que sin sanear se ejecuta con el origen
 * del LMS y puede leer el token de sesión. La base es el esquema de GitHub, que quita `script`,
 * `style`, `iframe`, `object`, `embed`, `form`, los atributos `on*` y cualquier URL que no sea
 * http(s) o mailto.
 */
import type { Element, Root } from 'hast';
import { defaultSchema } from 'rehype-sanitize';
import type { Options as SanitizeSchema } from 'rehype-sanitize';
import { visit } from 'unist-util-visit';

type RehypeTransform = (tree: Root) => void;

const CLOBBER_PREFIX = defaultSchema.clobberPrefix ?? 'user-content-';
const CLOBBERED_ATTRIBUTES = ['id', 'name'] as const;

export const llmHtmlSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  attributes: {
    ...defaultSchema.attributes,
    code: [['className', /^language-./, 'math-inline', 'math-display']],
  },
};

export const aiCanvasSanitizeSchema: SanitizeSchema = {
  ...llmHtmlSanitizeSchema,
  attributes: {
    ...llmHtmlSanitizeSchema.attributes,
    span: [['className', 'fr-math-v3', 'fr-draggable'], 'contentEditable', 'dataOriginalMath', 'draggable'],
  },
};

/**
 * remark-rehype ya prefija con `user-content-` los id de las notas al pie y sanitize los vuelve a
 * prefijar, lo que rompe los enlaces `#user-content-fn-1`. Quita el prefijo doble; los id escritos en
 * HTML crudo conservan su prefijo simple contra el DOM clobbering.
 */
export function rehypeCollapseClobberPrefix(): RehypeTransform {
  const doublePrefix = CLOBBER_PREFIX + CLOBBER_PREFIX;

  return function transform(tree: Root): void {
    visit(tree, 'element', function collapse(node: Element): void {
      CLOBBERED_ATTRIBUTES.forEach(function collapseAttribute(attribute): void {
        const value = node.properties?.[attribute];

        if (typeof value === 'string' && value.startsWith(doublePrefix)) {
          node.properties[attribute] = value.slice(CLOBBER_PREFIX.length);
        }
      });
    });
  };
}
